import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CONTACT_EMAIL } from "@/data/content";
import MotionToggle from "@/components/MotionToggle";

const MEASURES = [
  "Text and controls meet WCAG AA colour-contrast ratios.",
  "Every page works with a keyboard alone, with a visible focus outline and a “Skip to main content” link.",
  "Images carry text descriptions; decorative images and background videos are hidden from screen readers.",
  "Headings, landmarks and form labels are structured so screen readers can navigate them.",
  "Pop-up panels (the bag and the photo viewer) hold keyboard focus and close with the Escape key.",
  "Motion can be paused anywhere on the site, and the site follows your device’s “reduce motion” setting.",
  "Sound never plays on its own — the Maker garden sound and video sound only start when you turn them on.",
  "Every page is automatically re-checked against WCAG 2.2 AA each month, and issues found are fixed.",
];

const field = "w-full border-b border-ink/30 bg-transparent pb-3 pt-1 font-body text-base text-ink outline-none focus:border-ink";

function Feedback() {
  const [f, setF] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", { ...f, section: "Accessibility", projectType: "Accessibility feedback" });
      setSent(true);
      setF({ name: "", email: "", message: "" });
    } catch {
      toast.error(`Something went wrong. Please email ${CONTACT_EMAIL}.`);
    } finally {
      setBusy(false);
    }
  };

  if (sent) return <p role="status" data-testid="a11y-feedback-sent" className="font-display text-2xl font-extralight text-ink">Thank you — I’ll be in touch within 2 business days.</p>;
  return (
    <form onSubmit={submit} data-testid="a11y-feedback-form" className="grid gap-8 md:grid-cols-2">
      <label className="block"><span className="overline mb-2 block text-muted">Your name (required)</span>
        <input data-testid="a11y-feedback-name" required autoComplete="name" value={f.name} onChange={set("name")} className={field} /></label>
      <label className="block"><span className="overline mb-2 block text-muted">Email (required)</span>
        <input data-testid="a11y-feedback-email" type="email" required autoComplete="email" value={f.email} onChange={set("email")} className={field} /></label>
      <label className="block md:col-span-2"><span className="overline mb-2 block text-muted">What happened, and on which page? (required)</span>
        <textarea data-testid="a11y-feedback-message" required rows={4} value={f.message} onChange={set("message")} className={field} /></label>
      <button data-testid="a11y-feedback-submit" disabled={busy} className="w-fit border border-ink px-8 py-4 transition-colors hover:bg-ink hover:text-paper disabled:opacity-50">
        <span className="overline">{busy ? "Sending…" : "Send feedback"}</span>
      </button>
    </form>
  );
}

export default function Accessibility() {
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/a11y/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);
  const checked = status?.last_checked ? new Date(status.last_checked).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : null;

  return (
    <div className="px-6 pb-28 pt-36 md:px-12 md:pt-44">
      <article className="mx-auto max-w-3xl" data-testid="accessibility-page">
        <p className="overline text-clay">Accessibility statement</p>
        <h1 className="mt-4 font-display text-4xl font-extralight tracking-tight text-ink sm:text-5xl lg:text-6xl">This site is for everyone.</h1>
        <p className="mt-8 font-body text-base leading-relaxed text-stone">
          Phaedra Paulson, Phaedra Paulson · Maker and M. Kingsley Studio are committed to making this website usable by
          as many people as possible, including people with disabilities. The site is designed and tested to meet the
          <strong className="font-medium text-ink"> Web Content Accessibility Guidelines (WCAG) 2.2, Level AA</strong> — the
          standard used to measure accessibility under the Americans with Disabilities Act (ADA).
        </p>
        <p data-testid="a11y-last-checked" className="mt-4 font-body text-sm text-stone">
          {checked ? `Last automated check: ${checked}.` : "Automated checks run every month."} Reviewed and updated continuously.
        </p>

        <h2 className="mt-14 font-display text-2xl font-extralight text-ink md:text-3xl">What we’ve done</h2>
        <ul className="mt-6 list-disc space-y-3 pl-5 font-body text-base leading-relaxed text-stone">
          {MEASURES.map((m) => <li key={m}>{m}</li>)}
        </ul>

        <h2 className="mt-14 font-display text-2xl font-extralight text-ink md:text-3xl">Your settings</h2>
        <p className="mt-4 font-body text-base leading-relaxed text-stone">
          Prefer things still? Pause all animation, smooth scrolling and background video across the site. Your choice is remembered on this device.
        </p>
        <MotionToggle testid="a11y-motion-toggle" className="mt-5 border border-ink px-5 py-3 text-ink" />
        <p className="mt-4 font-body text-sm text-stone">You can also zoom up to 200% with your browser, and the layout reflows to fit.</p>

        <h2 className="mt-14 font-display text-2xl font-extralight text-ink md:text-3xl">Known limitations</h2>
        <p className="mt-4 font-body text-base leading-relaxed text-stone">
          Some short social videos contain music without speech and have no captions. The downloadable portfolio PDF is
          a visual lookbook; if you need its content in another format, ask and I’ll send it.
        </p>

        <h2 className="mt-14 font-display text-2xl font-extralight text-ink md:text-3xl">Need help or found a barrier?</h2>
        <p className="mt-4 mb-10 font-body text-base leading-relaxed text-stone">
          If anything on this site is hard to use, tell me and I’ll fix it or get you the information another way —
          including help placing an order. Email <a href={`mailto:${CONTACT_EMAIL}`} className="link-underline text-ink">{CONTACT_EMAIL}</a> or use this form.
          I aim to reply within 2 business days.
        </p>
        <Feedback />
      </article>
    </div>
  );
}
